import moment from "moment-timezone";
import IDaily_Route_Order_Map from "../models/IDaily_Route_Order_Map";
import IOrder from "../models/IOrder";
import IUserUI from "../models/IUserUI";
import OrderStatesEnum from "../models/OrderStatesEnum";
import UserTypesEnum from "../models/UserTypesEnum";
import SupplierService from "./SupplierService";

// Statuses that should be BLOCKED from daily routes entirely
export const BLOCKED_ORDER_STATUSES: OrderStatesEnum[] = [
    OrderStatesEnum.PRELIMINARY,
    OrderStatesEnum.SHIPPED,
    OrderStatesEnum.DELIVERED,
    OrderStatesEnum.PICKED_UP,
];

// Target "ready" statuses for daily routes
export const READY_STATUSES: OrderStatesEnum[] = [OrderStatesEnum.READY_FOR_DELIVERY, OrderStatesEnum.READY_FOR_PICKUP];

export interface IOrderStatusValidation {
    isBlocked: boolean;
    needsStatusChange: boolean;
    blockReason?: string;
    currentStatus?: OrderStatesEnum;
}

/**
 * Validates if an order can be added to a daily route
 */
const validateOrderForDailyRoute = (order: IOrder): IOrderStatusValidation => {
    const orderStateId = order.order_state_id;

    // Check if blocked
    if (orderStateId && BLOCKED_ORDER_STATUSES.includes(orderStateId)) {
        return {
            isBlocked: true,
            needsStatusChange: false,
            blockReason: getBlockedReason(orderStateId),
            currentStatus: orderStateId,
        };
    }

    // Check if needs status change (not in READY status)
    if (orderStateId && !READY_STATUSES.includes(orderStateId)) {
        return {
            isBlocked: false,
            needsStatusChange: true,
            currentStatus: orderStateId,
        };
    }

    return { isBlocked: false, needsStatusChange: false };
};

/**
 * Get human-readable reason why order is blocked (translation key)
 */
const getBlockedReason = (status: OrderStatesEnum): string => {
    switch (status) {
        case OrderStatesEnum.PRELIMINARY:
            return "order_is_preliminary";
        case OrderStatesEnum.SHIPPED:
            return "order_already_shipped";
        case OrderStatesEnum.DELIVERED:
            return "order_already_delivered";
        case OrderStatesEnum.PICKED_UP:
            return "order_already_picked_up";
        default:
            return "order_status_invalid_for_route";
    }
};

/**
 * Filter orders to remove blocked ones
 */
const filterBlockedOrders = (orders: IOrder[]): { validOrders: IOrder[]; blockedOrders: { order: IOrder; reason: string }[] } => {
    const validOrders: IOrder[] = [];
    const blockedOrders: { order: IOrder; reason: string }[] = [];

    for (const order of orders) {
        const validation = validateOrderForDailyRoute(order);
        if (validation.isBlocked) {
            blockedOrders.push({ order, reason: validation.blockReason! });
        } else {
            validOrders.push(order);
        }
    }

    return { validOrders, blockedOrders };
};

/**
 * Check if a single order's do_not_ship_before_date is after the route date.
 * The do_not_ship_before_date is stored as UTC in the DB, so we parse it as UTC
 * and convert to local time before comparing against the local route date.
 */
const isBeforeDoNotShipDate = (order: IOrder, routeDate: string): boolean => {
    if (!routeDate || !order.do_not_ship_before_date) return false;
    const dnsDate = moment.utc(order.do_not_ship_before_date).local().format("YYYY-MM-DD");
    return routeDate < dnsDate;
};

/**
 * Filter out orders whose do_not_ship_before_date is after the route date
 */
const filterDoNotShipBeforeOrders = (orders: IOrder[], routeDate: string): { validOrders: IOrder[]; excludedOrders: IOrder[] } => {
    const validOrders: IOrder[] = [];
    const excludedOrders: IOrder[] = [];

    for (const order of orders) {
        if (isBeforeDoNotShipDate(order, routeDate)) {
            excludedOrders.push(order);
        } else {
            validOrders.push(order);
        }
    }

    return { validOrders, excludedOrders };
};

/**
 * Get orders that need status change warning (not in READY_FOR_DELIVERY or READY_FOR_PICKUP)
 */
const getOrdersNeedingStatusChange = (orders: IOrder[]): IOrder[] => {
    return orders.filter((order) => {
        const validation = validateOrderForDailyRoute(order);
        return validation.needsStatusChange;
    });
};

/**
 * Fallback minutes a driver spends at a stop when neither the stop, the
 * customer, nor the supplier sets one. Mirrors the server migration default.
 */
export const DEFAULT_STOP_SERVICE_MINUTES = 15;

/**
 * Resolve the service (dwell) minutes for a stop:
 *   stop override -> customer default -> supplier default -> 15
 * Uses `??` deliberately: `0` is a legitimate "drop and go" override and must
 * never be treated as blank.
 */
const getEffectiveServiceMinutes = (
    stopMinutes: number | null | undefined,
    supplierDefault: number | null | undefined,
    vendorDefault?: number | null
): number => {
    return stopMinutes ?? vendorDefault ?? supplierDefault ?? DEFAULT_STOP_SERVICE_MINUTES;
};

// Supplier user types allowed to complete a daily route from the web. Mirrors the
// server's RouteService.ADMIN_COMPLETE_ROUTE_USER_TYPES, which enforces it.
export const ADMIN_COMPLETE_ROUTE_USER_TYPES: UserTypesEnum[] = [UserTypesEnum.ADMIN, UserTypesEnum.SHIP_MGMT];

const canAdminCompleteRoute = (userModel: IUserUI): boolean => {
    return userModel.isSupplierUser && SupplierService.isSupplierUserType(userModel.supplierUserToken, ADMIN_COMPLETE_ROUTE_USER_TYPES);
};

/**
 * Stops an admin completion will auto-skip: anything not yet completed, failed or skipped.
 */
const getIncompleteStops = (stops: IDaily_Route_Order_Map[] | undefined): IDaily_Route_Order_Map[] => {
    return (stops || []).filter((s) => !s.status || s.status === "pending" || s.status === "arrived");
};

const DailyRouteService = {
    validateOrderForDailyRoute,
    getBlockedReason,
    filterBlockedOrders,
    getOrdersNeedingStatusChange,
    isBeforeDoNotShipDate,
    filterDoNotShipBeforeOrders,
    getEffectiveServiceMinutes,
    canAdminCompleteRoute,
    getIncompleteStops,
    BLOCKED_ORDER_STATUSES,
    READY_STATUSES,
    DEFAULT_STOP_SERVICE_MINUTES,
    ADMIN_COMPLETE_ROUTE_USER_TYPES,
};

export default DailyRouteService;
